import React, { useState, useCallback } from 'react';
import mammoth from 'mammoth';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  Description,
  AutoFixHigh,
  UploadFileOutlined,
} from '@mui/icons-material';
import { InkyWorking } from './brand';
import { IconTile } from './pagekit';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/EnterpriseAuthContext';
import { useUsage } from '../context/UsageContext';
import { useAIRequests } from '../context/AIRequestContext';
import { hasReachedLimit } from '../services/usageService';
import aiService from '../services/aiService';
import edgeFunctionService from '../services/edgeFunctionService';

// Supabase Edge Functions cap request bodies at ~20MB; base64 adds ~33% overhead.
// 14MB raw keeps the encoded payload + JSON envelope safely under the limit.
const PPTX_MAX_BYTES = 14 * 1024 * 1024;

/**
 * Read a File's binary contents as a base64 string (no data URL prefix).
 * @param {File} file - The file to read.
 * @returns {Promise<string>} Base64-encoded bytes.
 */
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') {
        reject(new Error('Could not read file'));
        return;
      }
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

/**
 * True if the file name looks like a PowerPoint presentation.
 * @param {File | null | undefined} file - Uploaded file.
 * @returns {boolean}
 */
const isPptxFile = (file) => !!file && /\.pptx$/i.test(file.name);

/**
 * Optional Mammoth style maps: semantic mapping and highlights → &lt;mark&gt;.
 * Ordinary Word font RGB colour is not emitted by Mammoth by design; use app themes or future XML merge for that.
 * @see https://github.com/mwilliamson/mammoth.js
 */
const MAMMOTH_STYLE_MAP = [
  "p[style-name='Quote'] => blockquote:fresh",
  'highlight => mark',
];

/**
 * "Import a file as a template": Word/text samples are analyzed in the browser and saved as a
 * template; PowerPoint decks are queued for the server-side import job.
 * @param {object} props - Props.
 * @param {boolean} props.open - Visibility.
 * @param {function(): void} props.onClose - Close.
 * @param {function(object): void} [props.onTemplateCreated] - Called with the saved template.
 * @returns {JSX.Element} Dialogs.
 */
const TemplateAutoGenerator = ({ open, onClose, onTemplateCreated }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { actions } = useApp();
  const { state: authState } = useAuth();
  const { refreshUsage } = useUsage();
  const { createRequest, refreshRequests } = useAIRequests();

  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);

  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
  });

  const [pipelineBusy, setPipelineBusy] = useState(false);
  const [pipelineMessage, setPipelineMessage] = useState('');
  const [pipelineError, setPipelineError] = useState('');
  const [analysisInfo, setAnalysisInfo] = useState('');
  const [limitReachedOpen, setLimitReachedOpen] = useState(false);

  /**
   * Read uploaded file: Word via Mammoth (style map for quotes/highlight), plain text otherwise.
   * @param {File} file - User file.
   * @returns {Promise<object>} Structured content for `analyzeDocumentStructure`.
   */
  const readFileContent = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const fileName = file.name.toLowerCase();
      const isWordDocument = fileName.endsWith('.docx') || fileName.endsWith('.doc');

      if (isWordDocument) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const arrayBuffer = e.target.result;
            const result = await mammoth.convertToHtml({
              arrayBuffer,
              styleMap: MAMMOTH_STYLE_MAP,
            });

            if (result.messages && result.messages.length > 0) {
              console.warn('Mammoth conversion warnings:', result.messages);
            }

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = result.value;
            const plainText = tempDiv.textContent || tempDiv.innerText || '';

            resolve({
              html: result.value,
              text: plainText,
              isStructured: true,
              fileType: 'word',
            });
          } catch (error) {
            reject(new Error(`Failed to process Word document: ${error.message}`));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read Word document'));
        reader.readAsArrayBuffer(file);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            text: e.target.result,
            html: null,
            isStructured: false,
            fileType: 'text',
          });
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      }
    });
  }, []);

  const processUploadedFile = useCallback(
    async (file) => {
      if (!file) return;

      setUploadError('');
      setPipelineError('');
      setAnalysisInfo('');

      // PowerPoint decks take the async Cloud Run path; there's nothing meaningful to parse
      // in the browser (the worker extracts + restructures via AI on the server).
      if (isPptxFile(file)) {
        if (file.size > PPTX_MAX_BYTES) {
          setUploadError('PowerPoint file is too large (max 14MB). Please trim media or export a lighter .pptx.');
          setUploadedFile(null);
          setFileContent(null);
          return;
        }
        setUploadedFile(file);
        setFileContent({ fileType: 'pptx', isStructured: false });
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        setTemplateData((prev) => ({
          ...prev,
          name: prev.name?.trim() ? prev.name : `${baseName} Template`,
        }));
        return;
      }

      setUploadedFile(file);

      try {
        const content = await readFileContent(file);
        setFileContent(content);
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        setTemplateData((prev) => ({
          ...prev,
          name: prev.name?.trim() ? prev.name : `${baseName} Template`,
        }));
      } catch (error) {
        setUploadError(`Failed to read file content: ${error.message}. Please ensure the file is a valid document.`);
        setUploadedFile(null);
      }
    },
    [readFileContent]
  );

  const handleFileUpload = useCallback(
    async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      await processUploadedFile(file);
      event.target.value = '';
    },
    [processUploadedFile]
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragActive(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        processUploadedFile(file);
      }
    },
    [processUploadedFile]
  );

  /**
   * Queue the async PowerPoint import pipeline on Cloud Run.
   *
   * Creates an `ai_requests` row (type=`template_import`), dispatches the raw bytes to the
   * `import-presentation-template` Edge function, and closes the dialog. Progress is tracked
   * in the navbar jobs panel; completing the job opens the new draft template in the rich editor.
   *
   * @returns {Promise<void>}
   */
  const handlePptxImport = useCallback(async () => {
    const accountId = authState?.activeAccount?.id;
    if (!accountId) {
      setPipelineError('No active account — cannot queue the import.');
      return;
    }

    setPipelineBusy(true);
    setPipelineError('');
    setAnalysisInfo('');
    setPipelineMessage('Queuing PowerPoint import…');

    try {
      const b64 = await fileToBase64(uploadedFile);
      const aiRequest = await createRequest({
        type: 'template_import',
        title: `Importing ${uploadedFile.name}`,
        data: { fileName: uploadedFile.name, accountId },
      });
      if (!aiRequest?.id) {
        throw new Error('Could not create the import job.');
      }
      await edgeFunctionService.importPresentationTemplate({
        requestId: aiRequest.id,
        fileBase64: b64,
        fileName: uploadedFile.name,
        accountId,
        description: templateData.description?.trim() || undefined,
      });
      await refreshRequests();
      setPipelineMessage('');
      setAnalysisInfo(
        `Import started for ${uploadedFile.name}. Watch the jobs panel in the navbar — we'll open the draft template when AI finishes restructuring the deck.`
      );
      // Give the user a moment to read the confirmation, then dismiss.
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (error) {
      setPipelineError(error?.message || 'Could not queue PowerPoint import. Try again.');
    } finally {
      setPipelineBusy(false);
    }
  }, [authState?.activeAccount?.id, uploadedFile, templateData.description, createRequest, refreshRequests, onClose]);

  /**
   * Analyze gold document, build HTML template, save it, then redirect via `onTemplateCreated`.
   * @returns {Promise<void>}
   */
  const handleCreateTemplatePipeline = useCallback(async () => {
    if (!fileContent || !uploadedFile) return;

    const usageData = await refreshUsage();
    if (usageData && hasReachedLimit(usageData, 'templates')) {
      setLimitReachedOpen(true);
      return;
    }

    // PowerPoint decks take a completely different (async, server-side) pipeline.
    if (isPptxFile(uploadedFile)) {
      await handlePptxImport();
      return;
    }

    setPipelineBusy(true);
    setPipelineError('');
    setAnalysisInfo('');
    setPipelineMessage('Analyzing document…');

    try {
      const onLargeDocumentDetected = () => {
        setAnalysisInfo(
          'Large document: processing in chunks. This may take several minutes — please keep this dialog open.'
        );
      };

      // Deterministic analysis (task 'template.analyze', schema + seed); the server picks the
      // model from the account tier, so no model settings are injected here.
      const analysis = await aiService.analyzeDocumentStructure(
        fileContent,
        uploadedFile.name,
        onLargeDocumentDetected,
        { accountId: authState.activeAccount?.id }
      );

      const name =
        templateData.name.trim() ||
        analysis.documentTitle ||
        `${uploadedFile.name.replace(/\.[^/.]+$/, '')} Template`;
      const description =
        templateData.description.trim() ||
        `Template generated from ${analysis.metadata?.documentType || 'document'}: ${uploadedFile.name}`;

      setPipelineMessage('Generating template HTML…');
      const template = await aiService.generateTemplateFromAnalysis(analysis, name, description);

      setPipelineMessage('Saving template…');
      const result = await actions.createTemplate({
        name,
        description,
        content: template.content,
        source_document: {
          fileName: uploadedFile.name,
          analysis,
          generatedAt: new Date().toISOString(),
        },
      });

      if (result.success) {
        refreshUsage();
        if (typeof onTemplateCreated === 'function') {
          onTemplateCreated(result.data);
        }
        onClose();
      } else {
        setPipelineError(result.error || 'Failed to save template');
      }
    } catch (error) {
      setPipelineError(error.message || 'Template creation failed');
    } finally {
      setPipelineBusy(false);
      setPipelineMessage('');
    }
  }, [
    fileContent,
    uploadedFile,
    templateData.name,
    templateData.description,
    authState.activeAccount?.id,
    actions,
    refreshUsage,
    onTemplateCreated,
    onClose,
    handlePptxImport,
  ]);

  const handleClose = useCallback(() => {
    if (pipelineBusy) return;
    setUploadedFile(null);
    setFileContent(null);
    setUploadError('');
    setTemplateData({ name: '', description: '' });
    setPipelineError('');
    setAnalysisInfo('');
    onClose();
  }, [pipelineBusy, onClose]);

  const canUpload = fileContent && uploadedFile;
  const isDeck = isPptxFile(uploadedFile);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        aria-labelledby="template-auto-generator-title"
      >
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pr: 1.5 }}>
          <IconTile size={36}><UploadFileOutlined /></IconTile>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="template-auto-generator-title" component="h2" sx={{ fontSize: 17, fontWeight: 600 }}>
              Import a file as a template
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload a good example. Inky keeps its structure and turns the content into AI fields.
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={pipelineBusy} aria-label="Close" sx={{ mt: -0.5 }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '4px !important' }}>
          <Box
            component="label"
            htmlFor="template-auto-generator-file-input"
            onDragEnter={handleDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              position: 'relative',
              p: { xs: 2.5, sm: 3.5 },
              border: '1.5px dashed',
              borderColor: isDragActive ? 'var(--q-accent)' : 'var(--q-border-hover)',
              borderRadius: 'var(--q-radius-card)',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              cursor: pipelineBusy ? 'default' : 'pointer',
              bgcolor: isDragActive ? 'var(--q-accent-tint)' : 'var(--q-bg-subtle)',
              opacity: pipelineBusy ? 0.7 : 1,
              pointerEvents: pipelineBusy ? 'none' : 'auto',
              transition: 'border-color 120ms ease, background-color 120ms ease',
              '&:hover': { borderColor: 'var(--q-accent)' },
              '&:focus-within': { outline: '2px solid var(--q-focus-ring)', outlineOffset: 2 },
            }}
          >
            <input
              id="template-auto-generator-file-input"
              type="file"
              accept=".txt,.md,.doc,.docx,.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              onChange={handleFileUpload}
              disabled={pipelineBusy}
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}
            />
            <Box sx={{ width: 44, height: 44, borderRadius: 999, bgcolor: 'var(--q-surface)', border: '1px solid var(--q-border)', color: 'var(--q-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CloudUpload />
            </Box>
            <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {uploadedFile ? uploadedFile.name : 'Drop a file here or click to browse'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Word (.docx, .doc), text (.txt, .md) or PowerPoint (.pptx), up to 14MB
            </Typography>
          </Box>

          {uploadError && <Alert severity="error">{uploadError}</Alert>}

          {uploadedFile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Description fontSize="small" sx={{ color: 'var(--q-accent)' }} />
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                <strong>{uploadedFile.name}</strong> ({(uploadedFile.size / 1024).toFixed(1)} KB)
              </Typography>
              <Chip label={isDeck ? 'Imports in the background' : 'Ready'} color={isDeck ? 'info' : 'success'} size="small" />
            </Box>
          )}

          <TextField
            label="Template name"
            value={templateData.name}
            onChange={(e) => setTemplateData((prev) => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            disabled={pipelineBusy}
          />
          <TextField
            label="Description (optional)"
            value={templateData.description}
            onChange={(e) => setTemplateData((prev) => ({ ...prev, description: e.target.value }))}
            fullWidth
            disabled={pipelineBusy}
          />

          <Typography variant="caption" color="text.secondary">
            Word and text files are converted right here and open in the template editor. PowerPoint decks are
            restructured on the server; the jobs panel in the header tells you when the draft is ready.
          </Typography>

          {pipelineBusy && pipelineMessage && <InkyWorking label={pipelineMessage.replace(/…$/, '')} mood="writing" />}
          {!pipelineBusy && pipelineMessage && <Alert severity="info">{pipelineMessage}</Alert>}
          {analysisInfo && <Alert severity="info">{analysisInfo}</Alert>}
          {pipelineError && <Alert severity="error">{pipelineError}</Alert>}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleClose} disabled={pipelineBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTemplatePipeline}
            disabled={!canUpload || pipelineBusy}
            startIcon={pipelineBusy ? <CircularProgress size={16} color="inherit" /> : <AutoFixHigh />}
          >
            {pipelineBusy ? 'Working…' : isDeck ? 'Start import' : 'Create template'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={limitReachedOpen} onClose={() => setLimitReachedOpen(false)}>
        <DialogTitle>Template limit reached</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have reached your template limit. Upgrade your plan to create more templates.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitReachedOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setLimitReachedOpen(false);
              onClose();
              window.dispatchEvent(new Event('quillai:open-settings'));
            }}
          >
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateAutoGenerator;
